import { Alert, Button, Card, Col, Row, Statistic, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Summary = {
  lesson_plans: number;
  lesson_plans_draft: number;
  homework: number;
  homework_published: number;
  submissions: number;
  submissions_awaiting_feedback: number;
  resources: number;
  class_announcements: number;
};

export default function LmsHubPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { canManage, canView } = useLmsAccess();
  const summary = useQuery({
    queryKey: ['education-lms-summary'],
    enabled: canView,
    queryFn: async () => (await api.get<Summary>('/education/lms/summary')).data,
  });
  const s = summary.data;

  const cards = [
    {
      path: '/education/lms/lesson-plans',
      newPath: '/education/lms/lesson-plans/new',
      title: t('lmsLessonPlans', { defaultValue: 'Lesson plans' }),
      desc: t('lmsLessonPlansIntro', { defaultValue: 'Plan lessons per section and subject.' }),
      count: s?.lesson_plans,
      newLabel: t('lmsNewLessonPlan', { defaultValue: 'New lesson plan' }),
    },
    {
      path: '/education/lms/homework',
      newPath: '/education/lms/homework/new',
      title: t('lmsHomework', { defaultValue: 'Homework' }),
      desc: t('lmsHomeworkIntro', { defaultValue: 'Publish homework, review submissions, return feedback.' }),
      count: s?.homework,
      extra: s ? `${t('lmsSummaryAwaiting', { defaultValue: 'Awaiting feedback' })}: ${s.submissions_awaiting_feedback}` : undefined,
      newLabel: t('lmsNewHomework', { defaultValue: 'New homework' }),
    },
    {
      path: '/education/lms/resources',
      newPath: '/education/lms/resources/new',
      title: t('lmsResources', { defaultValue: 'Resources' }),
      desc: t('lmsResourcesIntro', { defaultValue: 'Shared links to learning material.' }),
      count: s?.resources,
      newLabel: t('lmsNewResource', { defaultValue: 'New resource' }),
    },
    {
      path: '/education/lms/announcements',
      newPath: '/education/lms/announcements/new',
      title: t('lmsAnnouncements', { defaultValue: 'Class announcements' }),
      desc: t('lmsAnnouncementsIntro', { defaultValue: 'Section-level notices.' }),
      count: s?.class_announcements,
      newLabel: t('lmsNewAnnouncement', { defaultValue: 'New announcement' }),
    },
  ];

  return (
    <EducationPageChrome
      title={t('lms', { defaultValue: 'Classroom (LMS)' })}
      description={t('lmsIntro', {
        defaultValue: 'Lightweight classroom tools. Links only — no file vault, video, or grading marks.',
      })}
      extra={<Button onClick={() => navigate('/education')}>{t('backToEducation', { defaultValue: 'Education home' })}</Button>}
    >
      {!canManage ? (
        <Alert type="info" showIcon message={t('lmsReadOnly', { defaultValue: 'Read-only access.' })} />
      ) : null}
      <Row gutter={[16, 16]}>
        {cards.map((card) => (
          <Col xs={24} md={12} key={card.path}>
            <Card size="small" title={card.title} loading={summary.isLoading}>
              <Statistic value={card.count ?? 0} />
              <Typography.Paragraph type="secondary">{card.desc}</Typography.Paragraph>
              {card.extra ? <Typography.Paragraph type="secondary">{card.extra}</Typography.Paragraph> : null}
              <Button type="primary" onClick={() => navigate(card.path)}>
                {t('open', { defaultValue: 'Open' })}
              </Button>{' '}
              {canManage ? <Button onClick={() => navigate(card.newPath)}>{card.newLabel}</Button> : null}
            </Card>
          </Col>
        ))}
      </Row>
    </EducationPageChrome>
  );
}
